import log from '../lib/logger';
import ApplicationController from './application_controller';

/**
 * Polls the financial statement request status endpoint while the user waits
 * on the "Generating Financial Statement" pending page, then redirects to the
 * finished statement.
 *
 * This replaces an inline <script> whose self-rescheduling setTimeout chain
 * survived Turbo Drive body swaps: it kept polling after the user navigated
 * away and, on completion, yanked them off whatever page they were on via
 * window.location.href. Revisiting the pending page stacked concurrent loops.
 * As a Stimulus controller, disconnect() clears the pending timer on every
 * body swap, and the completion redirect only fires while the controller's
 * element is still in the document.
 *
 * Usage:
 *   <div data-controller="financial-statement-poller"
 *        data-financial-statement-poller-url-value="/orgs/1/financial-statement-requests/2/status.json"
 *        data-financial-statement-poller-dashboard-url-value="/orgs/1/dashboards/billing">
 *     <span data-financial-statement-poller-target="status">Pending</span>
 *   </div>
 */
export default class extends ApplicationController {
  static targets = ['status'];

  static values = {
    url: String,
    dashboardUrl: String,
    maxPolls: { type: Number, default: 120 },
    initialDelay: { type: Number, default: 2000 },
    pollingDelay: { type: Number, default: 3000 },
    errorDelay: { type: Number, default: 5000 },
  };

  connect() {
    if (this.isTurboPreview) return;

    this.pollCount = 0;
    this.schedule(this.initialDelayValue);
  }

  disconnect() {
    this.cancel();
  }

  schedule(delay) {
    this.cancel();
    this.timer = setTimeout(() => this.checkStatus(), delay);
  }

  cancel() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  async checkStatus() {
    this.timer = null;
    this.pollCount += 1;

    if (this.pollCount > this.maxPollsValue) {
      this.updateStatus('Taking longer than expected');
      return;
    }

    let data;
    try {
      const response = await fetch(this.urlValue, { headers: { Accept: 'application/json' } });
      data = await response.json();
    } catch (error) {
      log.error('Error checking financial statement status:', error);
      if (this.element.isConnected) this.schedule(this.errorDelayValue);
      return;
    }

    // The user may have Turbo-navigated away while the request was in flight;
    // never redirect, re-render, or reschedule from a page that is no longer
    // displayed.
    if (!this.element.isConnected) return;

    log.debug('Financial statement status check:', data);

    if (data.completed && data.redirect_url) {
      this.redirect(data.redirect_url);
    } else if (data.failed) {
      this.renderFailure(data.error_message);
    } else {
      const badgeClass = data.status === 'processing' ? 'badge badge-primary' : 'badge badge-warning';
      this.updateStatus(this.humanize(data.status), badgeClass);
      this.schedule(this.pollingDelayValue);
    }
  }

  // Isolated so specs can stub navigation (jsdom cannot follow location changes).
  redirect(url) {
    window.location.href = url;
  }

  updateStatus(text, className = null) {
    if (!this.hasStatusTarget) return;

    if (className) this.statusTarget.className = className;
    this.statusTarget.textContent = text;
  }

  renderFailure(message) {
    const alert = document.createElement('div');
    alert.className = 'alert alert-danger';
    const icon = document.createElement('i');
    icon.className = 'bi bi-exclamation-triangle mr-2';
    alert.append(icon, `Failed to generate report: ${message || 'Unknown error'}`);

    const link = document.createElement('a');
    link.href = this.dashboardUrlValue;
    link.className = 'btn btn-primary';
    link.textContent = 'Return to Dashboard';

    this.element.replaceChildren(alert, link);
  }

  humanize(status) {
    return status.charAt(0).toUpperCase() + status.slice(1);
  }
}
